import { useCallback, useMemo } from "react";
import { formatValue, asNumber } from "../../lib/kernelFormat.js";
import { CanvasSurface, KernelEmpty, resolveCssColor, fillStippleRect } from "./shared.jsx";

export const kind = "boxplot";

// Per-row quantile boxplot. p50 is the center line, p95 the box edge,
// p99 (or max if provided) the whisker. Stacked vertically so each
// row is one labelled distribution.
export function Component({ spec, data }) {
    const series = useMemo(() => buildSeries(spec, data), [spec, data]);
    const rowH = 20;
    const padT = 4;
    const padB = 22;
    const height = padT + padB + series.rows.length * rowH;

    const draw = useCallback((ctx, { width, height: h, root }) => {
        if (!series.rows.length) return;
        const fg = resolveCssColor(root, spec.color || "var(--green)") || "#5a8c5e";
        const ink = resolveCssColor(root, "var(--ink)") || "#fff";
        const muted = resolveCssColor(root, "var(--plot-ink-soft)") || "#888";
        const line = resolveCssColor(root, "var(--line-soft)") || "#333";

        const padL = 88;
        const padR = 10;
        const gx = Math.max(1, width - padL - padR);
        const gy = h - padT - padB;
        const lo = series.lo;
        const hi = series.hi;
        const range = hi - lo || 1;
        const xOf = (v) => padL + ((v - lo) / range) * gx;

        // axis baseline
        ctx.strokeStyle = line;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(padL, padT + gy);
        ctx.lineTo(padL + gx, padT + gy);
        ctx.stroke();

        ctx.font = "10px ui-monospace, SFMono-Regular, Menlo, monospace";
        ctx.fillStyle = muted;
        ctx.textBaseline = "top";
        ctx.textAlign = "center";
        for (let i = 0; i <= 4; i++) {
            const t = i / 4;
            const v = lo + range * t;
            ctx.fillText(formatTick(v, spec.format), padL + t * gx, padT + gy + 4);
        }

        // rows
        for (let i = 0; i < series.rows.length; i++) {
            const row = series.rows[i];
            const cy = padT + i * rowH + rowH / 2;
            ctx.textBaseline = "middle";
            ctx.textAlign = "right";
            ctx.fillStyle = ink;
            ctx.fillText(truncate(String(row.label), 14), padL - 8, cy);

            const minX = xOf(row.min ?? row.p50);
            const maxX = xOf(row.max ?? row.p99);
            const boxL = xOf(Math.min(row.p50, row.p95));
            const boxR = xOf(Math.max(row.p50, row.p95));

            // whiskers
            ctx.strokeStyle = muted;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(minX, cy);
            ctx.lineTo(maxX, cy);
            ctx.stroke();
            // whisker caps
            ctx.beginPath();
            ctx.moveTo(minX, cy - 4); ctx.lineTo(minX, cy + 4);
            ctx.moveTo(maxX, cy - 4); ctx.lineTo(maxX, cy + 4);
            ctx.stroke();

            // box (p50 → p95) — stippled fill matches SurfaceChart
            const boxX = boxL;
            const boxY = cy - 6;
            const boxW = Math.max(1, boxR - boxL);
            const boxH = 12;
            fillStippleRect(ctx, boxX, boxY, boxW, boxH, fg, { density: 0.9 });
            ctx.strokeStyle = fg;
            ctx.strokeRect(boxX, boxY, boxW, boxH);

            // p50 marker
            const m = xOf(row.p50);
            ctx.strokeStyle = ink;
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.moveTo(m, cy - 6);
            ctx.lineTo(m, cy + 6);
            ctx.stroke();
        }
    }, [series, spec.color, spec.format]);

    if (!series.rows.length) return <KernelEmpty message={spec.empty || "no rows"} />;

    return (
        <div className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[10px] py-[8px]">
            <CanvasSurface
                draw={draw}
                deps={[series, spec.color, spec.format]}
                height={height}
                className="w-full bg-[color:var(--plot-bg)] rounded-[2px] border border-[color:var(--line-soft)]"
            />
        </div>
    );
}

function buildSeries(spec, data) {
    const arr = Array.isArray(data) ? data : [];
    const rows = [];
    let lo = Infinity, hi = -Infinity;
    for (const row of arr) {
        if (!row) continue;
        const p50 = asNumber(row[spec.p50Field]);
        const p95 = asNumber(row[spec.p95Field]);
        const p99 = asNumber(row[spec.p99Field]);
        const min = asNumber(row[spec.minField]) ?? p50;
        const max = asNumber(row[spec.maxField]) ?? p99 ?? p95 ?? p50;
        if (p50 == null || p95 == null) continue;
        rows.push({ label: row[spec.labelField] ?? "", p50, p95, p99, min, max });
        if (min != null && min < lo) lo = min;
        if (max != null && max > hi) hi = max;
    }
    if (!rows.length) return { rows: [], lo: 0, hi: 1 };
    if (lo === Infinity) lo = 0;
    if (hi === -Infinity) hi = 1;
    if (lo === hi) { lo = lo - 1; hi = hi + 1; }
    return { rows, lo, hi };
}

function formatTick(v, format) {
    if (format) return formatValue(v, format);
    const n = Number(v);
    if (!Number.isFinite(n)) return "";
    if (Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
    if (Number.isInteger(n)) return n.toString();
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function truncate(s, n) {
    if (s.length > n) return s.slice(0, n - 1) + "…";
    return s;
}

function withAlpha(color, alpha) {
    if (!color) return color;
    if (color.startsWith("rgba(")) {
        return color.replace(/rgba\(([^)]+)\)/, (m, inner) => {
            const parts = inner.split(",").map((s) => s.trim());
            parts[3] = String(alpha);
            return `rgba(${parts.join(", ")})`;
        });
    }
    if (color.startsWith("rgb(")) {
        return color.replace("rgb(", "rgba(").replace(")", `, ${alpha})`);
    }
    if (color.startsWith("#")) {
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        if (Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b)) {
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
    }
    return color;
}
